import { apiMessage, metric, postJson } from '../api';
import { L, expireSession, resetHistory, state, touchSession } from '../state';
import { listen, speak } from '../speech';
import { $, escapeHtml, modalBody, need, show } from '../ui/modal';
import { booking } from './booking';
import { interpreter } from './interpreter';
import { withAINotice } from './notice';
import type { ChatResponse } from '../types';

function addMessage(kind: 'user' | 'ai', text: string): HTMLDivElement {
  const messages = need<HTMLDivElement>('#msgs');
  const el = document.createElement('div');
  el.className = `msg ${kind}`;
  el.textContent = text;
  messages.append(el);
  messages.scrollTop = messages.scrollHeight;
  return el;
}

function appendSources(target: HTMLElement, sources: ChatResponse['sources']): void {
  if (!sources?.length) return;
  const box = document.createElement('div');
  box.className = 'sourcebox';
  const label = document.createElement('strong');
  label.textContent = `${L().sources}: `;
  box.append(label);

  let shown = 0;
  for (const source of sources.slice(0, 4)) {
    if (!/^https?:\/\//i.test(source.url)) continue;
    shown++;
    const link = document.createElement('a');
    link.href = source.url;
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    let host = '';
    try { host = new URL(source.url).hostname.replace(/^www\./, ''); } catch { /* url rara */ }
    link.textContent = `[${shown}] ${String(source.title || host || `Source ${shown}`).trim().slice(0, 70)}`;
    link.title = source.url;
    link.addEventListener('click', () => void metric('source_open'));
    box.append(document.createElement('br'), link);
  }
  if (shown) target.append(box);
}

function appendSmartActions(target: HTMLElement, data: ChatResponse): void {
  const lang = L();
  const actions = document.createElement('div');
  actions.className = 'ai-actions';
  let count = 0;

  if (data.booking_ready || data.intent === 'booking') {
    const button = document.createElement('button');
    button.className = 'mini smart-cta';
    button.type = 'button';
    button.textContent = `📅 ${lang.bookTitle}`;
    button.onclick = () => { void metric('booking_ai_cta'); booking(); };
    actions.append(button);
    count++;
  }
  if (data.intent === 'translation') {
    const button = document.createElement('button');
    button.className = 'mini';
    button.type = 'button';
    button.textContent = `🎙️ ${lang.interpreter}`;
    button.onclick = () => withAINotice(interpreter);
    actions.append(button);
    count++;
  }
  if (count) target.append(actions);
}

export function chat(): void {
  void metric('chat_open');
  expireSession();
  const lang = L();

  show('Talkxi', `
    <div class="chattools">
      <span class="ai-badge">AI</span>
      <button id="newChat" class="mini" type="button">↻ ${escapeHtml(lang.newChat)}</button>
    </div>
    <div class="messages" id="msgs"></div>
    ${state.history.length ? '' : `<div class="chips" id="chips">${
      lang.extra.chips.map((c) => `<button type="button" data-chip="${escapeHtml(c)}">${escapeHtml(c)}</button>`).join('')
    }</div>`}
    <form id="cf" class="chatrow">
      <button id="chatMic" class="micbtn" type="button" aria-label="${escapeHtml(lang.interpreter)}">🎙️</button>
      <input id="q" autocomplete="off" placeholder="${escapeHtml(lang.ph)}" required maxlength="2000">
      <button class="cta">${escapeHtml(lang.send)}</button>
    </form>`);

  addMessage('ai', lang.hello);
  for (const message of state.history) {
    addMessage(message.role === 'user' ? 'user' : 'ai', message.content);
  }

  need('#newChat').onclick = () => { resetHistory(); chat(); };
  for (const chip of modalBody().querySelectorAll<HTMLButtonElement>('[data-chip]')) {
    chip.onclick = () => {
      need<HTMLInputElement>('#q').value = chip.dataset.chip ?? '';
      need<HTMLFormElement>('#cf').requestSubmit();
    };
  }

  const input = need<HTMLInputElement>('#q');
  const mic = need<HTMLButtonElement>('#chatMic');

  mic.onclick = () => {
    mic.classList.add('rec');
    input.placeholder = lang.listening;
    const started = listen({
      locale: lang.locale,
      onResult: (transcript) => {
        input.value = transcript;
        need<HTMLFormElement>('#cf').requestSubmit();
      },
      onError: (kind) => { input.placeholder = kind === 'denied' ? lang.micHelp : lang.ph; },
      onEnd: () => {
        mic.classList.remove('rec');
        if (input.placeholder === lang.listening) input.placeholder = lang.ph;
      },
    });
    if (!started) {
      mic.classList.remove('rec');
      input.placeholder = lang.voiceUnavailable;
    }
  };

  input.focus();

  need<HTMLFormElement>('#cf').onsubmit = async (event) => {
    event.preventDefault();
    expireSession();
    const question = input.value.trim();
    if (!question) return;

    touchSession();
    addMessage('user', question);
    input.value = '';
    state.history.push({ role: 'user', content: question });
    void metric('chat_sent');

    const waiting = addMessage('ai', '…');
    try {
      const { ok, data } = await postJson<ChatResponse & { error?: string; message?: string }>('/api/chat.php', {
        messages: state.history.slice(-12),
        languageName: lang.name,
        route: { from: state.route.from, to: state.route.to },
        webSearch: state.webSearch,
      });

      if (!ok || !data.client) {
        void metric('chat_error');
        waiting.textContent = apiMessage(data, lang.err, lang.form.auth);
        return;
      }

      void metric('chat_ok');
      waiting.textContent = data.client;
      state.history.push({ role: 'assistant', content: data.client });
      touchSession();
      $('#chips')?.remove();
      appendSources(waiting, data.sources);
      appendSmartActions(waiting, data);

      const speakButton = document.createElement('button');
      speakButton.className = 'speakbtn';
      speakButton.type = 'button';
      speakButton.textContent = '🔊';
      speakButton.setAttribute('aria-label', 'Escuchar');
      speakButton.onclick = () => speak(data.client, lang.locale, true);
      waiting.append(' ', speakButton);

      if (state.autoVoice) speak(data.client, lang.locale);

      if (data.driver && data.driver !== 'Sin mensaje para el conductor.') {
        addMessage('ai', `🚕 ${lang.driver}: ${data.driver}`).classList.add('driver-note');
      }
    } catch {
      void metric('chat_error');
      waiting.textContent = lang.err;
    }
  };
}
